/**
 * Global serialised signEvent queue.
 *
 * NIP-46 / NIP-07 signers (bark, nsec.app, signet bunker) often get
 * stuck or rate-limit when hit with concurrent signEvent requests.
 * The heartbeat (4s), per-wave replay chunks, ghost publishes, NIP-53
 * announces, and user-initiated claim signs can all overlap in time.
 *
 * This module wraps a signer so every signEvent call is processed
 * strictly one-at-a-time, with a small post-sign delay to let the
 * signer's service-worker / port settle before the next call. Knock-on
 * effect: when bark's background script naps, only ONE request waits
 * on it instead of all of them piling up and timing out together.
 *
 * The wrapped signer keeps the same interface as the original — no
 * call site changes. Just wrap once at session-set time.
 */

import type { SignetSigner } from 'signet-login';
type Signer = SignetSigner;

/** Min gap between consecutive sign attempts in ms. Lets bark's service
 *  worker write its response back through the messaging port before
 *  the next request lands. 50ms is empirically enough; raising it
 *  helps with stuck signers at the cost of slowing batch publishes. */
const POST_SIGN_GAP_MS = 80;

/** Hard per-task timeout. If orig() doesn't resolve in this long, the
 *  queue rejects the task with 'queue-timeout' and moves on to the
 *  next. Without this the runner would `await orig(task.template)`
 *  indefinitely on a stuck bark, blocking every subsequent sign for
 *  the rest of the page session — claim signs would queue behind a
 *  failed replay retry and the user would be unable to bank sats.
 *
 *  45s is slightly longer than the longest call-site timeout (30s for
 *  claim + replay), so call-site Promise.race bails first with a
 *  domain-specific error; the queue timeout is the backstop that
 *  unblocks the next sign for the queue when orig() is genuinely
 *  wedged. */
const QUEUE_TIMEOUT_MS = 45_000;

interface QueuedSign {
  // The queue is shared by all accounts. Bind the signing operation to the
  // request, rather than whichever signer happened to start the queue runner.
  sign: Signer['signEvent'];
  template: Parameters<Signer['signEvent']>[0];
  resolve: (event: Awaited<ReturnType<Signer['signEvent']>>) => void;
  reject: (err: unknown) => void;
}

const queue: QueuedSign[] = [];
let processing = false;
let inFlight = false;
let lastSignFinishedAt = 0;

function runQueue(): void {
  if (processing) return;
  processing = true;
  void (async () => {
    while (queue.length > 0) {
      const task = queue.shift()!;
      // Respect the post-sign gap even across re-entry.
      const gap = Math.max(0, POST_SIGN_GAP_MS - (Date.now() - lastSignFinishedAt));
      if (gap > 0) await new Promise((r) => setTimeout(r, gap));
      inFlight = true;
      // Race this request's signer against the queue's hard timeout. Its promise
      // may keep running in the background after we move on (we can't
      // cancel a NIP-07 extension's signEvent), but `await` releases so
      // the next task gets its turn. Caller already rejected via its
      // own call-site timeout by this point.
      try {
        let timer: number | undefined;
        const timeout = new Promise<never>((_, reject) => {
          timer = window.setTimeout(
            () => reject(new Error('queue-timeout')),
            QUEUE_TIMEOUT_MS,
          );
        });
        try {
          const ev = await Promise.race([task.sign(task.template), timeout]);
          task.resolve(ev);
        } catch (err) {
          task.reject(err);
        } finally {
          if (timer !== undefined) window.clearTimeout(timer);
        }
      } finally {
        inFlight = false;
        lastSignFinishedAt = Date.now();
      }
    }
    processing = false;
  })();
}

/** Wrap a signer so its signEvent is serialised globally. Returns a
 *  new Signer object that delegates everything else (capabilities,
 *  encrypt/decrypt) straight to the original.
 *
 *  IMPORTANT: call exactly once per session at session-set time. The
 *  queue is module-scoped, so a second wrap layer creates a queue of
 *  queues and serialises through twice (correct but wasteful). */
export function serialiseSigner(signer: Signer): Signer {
  const origSignEvent = signer.signEvent.bind(signer);
  return new Proxy(signer, {
    get(target, prop, receiver): unknown {
      if (prop === 'signEvent') {
        return (template: Parameters<Signer['signEvent']>[0]) => new Promise<Awaited<ReturnType<Signer['signEvent']>>>((resolve, reject) => {
          queue.push({ sign: origSignEvent, template, resolve, reject });
          runQueue();
        });
      }
      return Reflect.get(target, prop, receiver);
    },
  });
}

/** Number of pending sign requests waiting on the queue (excluding any
 *  currently in flight). Surfaced for UI debugging — a stuck signer
 *  shows as a growing pending count. */
export function pendingSignCount(): number {
  return queue.length + (inFlight ? 1 : 0);
}
